import React, { useState } from 'react'
import {
  Grid, Card, Icon, Message, Segment, Header, Label,
  Button, Divider, Transition, Step, Popup
} from 'semantic-ui-react'

const UserFlowDiagram = ({ activeView }) => {
  const [hoveredCard, setHoveredCard] = useState(null)

  // Define process steps for each user type
  const bookCreatorSteps = [
    {
      id: 'bc-setup',
      icon: 'user plus',
      title: 'Create Account',
      description: 'Register as Book Creator',
      color: 'blue',
      nextStep: 'bc-litgroup'
    },
    {
      id: 'bc-litgroup',
      icon: 'building',
      title: 'Setup Literary Group',
      description: 'Create publisher/author brand',
      color: 'blue',
      nextStep: 'bc-import'
    },
    {
      id: 'bc-import',
      icon: 'upload',
      title: 'Import Book PDF',
      description: 'Upload manuscript for processing',
      color: 'blue',
      nextStep: 'bc-process'
    },
    {
      id: 'bc-process',
      icon: 'cogs',
      title: 'Automatic Processing',
      description: 'Extract text & identify characters',
      color: 'blue',
      automated: true,
      nextStep: 'bc-review'
    },
    {
      id: 'bc-review',
      icon: 'eye',
      title: 'Review Characters',
      description: 'Verify extracted character list',
      color: 'blue',
      nextStep: 'bc-decision-post'
    },
    {
      id: 'bc-decision-post',
      icon: 'question circle',
      title: 'Post Opportunity?',
      description: 'Decide to create public listing',
      color: 'yellow',
      isDecision: true,
      yesPath: 'bc-opportunity',
      noPath: 'bc-private'
    },
    {
      id: 'bc-opportunity',
      icon: 'bullhorn',
      title: 'Post Opportunity',
      description: 'Create public voice actor listing',
      color: 'blue',
      nextStep: 'bc-wait'
    },
    {
      id: 'bc-private',
      icon: 'lock',
      title: 'Keep Private',
      description: 'Direct hire or abandon project',
      color: 'grey',
      isEnd: true
    },
    {
      id: 'bc-wait',
      icon: 'clock',
      title: 'Await Applications',
      description: 'Wait for voice actor submissions',
      color: 'teal',
      isWaiting: true,
      nextStep: 'bc-review-apps'
    },
    {
      id: 'bc-review-apps',
      icon: 'users',
      title: 'Review Applications',
      description: 'Evaluate voice samples & rates',
      color: 'blue',
      nextStep: 'bc-decision-quality'
    },
    {
      id: 'bc-decision-quality',
      icon: 'balance scale',
      title: 'Quality Acceptable?',
      description: 'Evaluate application quality',
      color: 'yellow',
      isDecision: true,
      yesPath: 'bc-decision-select',
      noPath: 'bc-reject'
    },
    {
      id: 'bc-decision-select',
      icon: 'trophy',
      title: 'Best Candidate?',
      description: 'Select winning application',
      color: 'yellow',
      isDecision: true,
      yesPath: 'bc-accept',
      noPath: 'bc-reject'
    },
    {
      id: 'bc-accept',
      icon: 'check circle',
      title: 'Send Acceptance',
      description: 'Notify selected voice actor',
      color: 'green',
      isInteraction: true,
      nextStep: 'bc-negotiate'
    },
    {
      id: 'bc-reject',
      icon: 'times circle',
      title: 'Send Rejection',
      description: 'Professional feedback provided',
      color: 'red',
      isInteraction: true,
      nextStep: 'bc-wait'
    },
    {
      id: 'bc-negotiate',
      icon: 'handshake',
      title: 'Negotiate Contract',
      description: 'Finalize terms & payment',
      color: 'purple',
      isInteraction: true,
      nextStep: 'bc-project'
    },
    {
      id: 'bc-project',
      icon: 'tasks',
      title: 'Manage Project',
      description: 'Oversee recording process',
      color: 'blue',
      nextStep: 'bc-review-delivery'
    },
    {
      id: 'bc-review-delivery',
      icon: 'headphones',
      title: 'Review Delivery',
      description: 'Check audio quality',
      color: 'blue',
      nextStep: 'bc-decision-approve'
    },
    {
      id: 'bc-decision-approve',
      icon: 'question circle',
      title: 'Approve Quality?',
      description: 'Accept or request retake',
      color: 'yellow',
      isDecision: true,
      yesPath: 'bc-payment',
      noPath: 'bc-retake'
    },
    {
      id: 'bc-payment',
      icon: 'dollar',
      title: 'Process Payment',
      description: 'Release milestone payment',
      color: 'green',
      nextStep: 'bc-complete'
    },
    {
      id: 'bc-retake',
      icon: 'redo',
      title: 'Request Retake',
      description: 'Provide specific feedback',
      color: 'orange',
      nextStep: 'bc-review-delivery'
    },
    {
      id: 'bc-complete',
      icon: 'star',
      title: 'Project Complete',
      description: 'Leave review & rating',
      color: 'green',
      isEnd: true
    }
  ]

  const voiceActorSteps = [
    {
      id: 'va-setup',
      icon: 'user circle',
      title: 'Create Profile',
      description: 'Register as Voice Actor',
      color: 'green',
      nextStep: 'va-portfolio'
    },
    {
      id: 'va-portfolio',
      icon: 'microphone',
      title: 'Build Portfolio',
      description: 'Upload voice samples & demos',
      color: 'green',
      nextStep: 'va-browse'
    },
    {
      id: 'va-browse',
      icon: 'search',
      title: 'Browse Opportunities',
      description: 'Filter by genre & rate',
      color: 'green',
      nextStep: 'va-decision-found'
    },
    {
      id: 'va-decision-found',
      icon: 'question circle',
      title: 'Found Match?',
      description: 'Identify suitable opportunity',
      color: 'yellow',
      isDecision: true,
      yesPath: 'va-review-op',
      noPath: 'va-browse'
    },
    {
      id: 'va-review-op',
      icon: 'clipboard list',
      title: 'Review Details',
      description: 'Check requirements & budget',
      color: 'green',
      nextStep: 'va-decision-apply'
    },
    {
      id: 'va-decision-apply',
      icon: 'question circle',
      title: 'Apply?',
      description: 'Decide to submit application',
      color: 'yellow',
      isDecision: true,
      yesPath: 'va-apply',
      noPath: 'va-browse'
    },
    {
      id: 'va-apply',
      icon: 'edit',
      title: 'Submit Application',
      description: 'Send samples & proposed rate',
      color: 'green',
      isInteraction: true,
      nextStep: 'va-wait'
    },
    {
      id: 'va-wait',
      icon: 'clock outline',
      title: 'Await Response',
      description: 'Wait for creator decision',
      color: 'teal',
      isWaiting: true,
      nextStep: 'va-receive'
    },
    {
      id: 'va-receive',
      icon: 'envelope',
      title: 'Receive Status',
      description: 'Get acceptance or rejection',
      color: 'green',
      isInteraction: true,
      nextStep: 'va-decision-accepted'
    },
    {
      id: 'va-decision-accepted',
      icon: 'question circle',
      title: 'Accepted?',
      description: 'Check application status',
      color: 'yellow',
      isDecision: true,
      yesPath: 'va-negotiate',
      noPath: 'va-end-rejected'
    },
    {
      id: 'va-end-rejected',
      icon: 'times',
      title: 'Application Rejected',
      description: 'Learn from feedback',
      color: 'red',
      isEnd: true
    },
    {
      id: 'va-negotiate',
      icon: 'comments',
      title: 'Negotiate Terms',
      description: 'Finalize rate & schedule',
      color: 'purple',
      isInteraction: true,
      nextStep: 'va-record'
    },
    {
      id: 'va-record',
      icon: 'headphones',
      title: 'Record Audio',
      description: 'Create character voices',
      color: 'green',
      nextStep: 'va-deliver'
    },
    {
      id: 'va-deliver',
      icon: 'box',
      title: 'Deliver Files',
      description: 'Upload completed recordings',
      color: 'green',
      isInteraction: true,
      nextStep: 'va-decision-retake'
    },
    {
      id: 'va-decision-retake',
      icon: 'question circle',
      title: 'Retake Needed?',
      description: 'Check if revisions requested',
      color: 'yellow',
      isDecision: true,
      yesPath: 'va-retake',
      noPath: 'va-payment'
    },
    {
      id: 'va-retake',
      icon: 'redo',
      title: 'Record Retake',
      description: 'Address feedback issues',
      color: 'orange',
      nextStep: 'va-deliver'
    },
    {
      id: 'va-payment',
      icon: 'money',
      title: 'Receive Payment',
      description: 'Get milestone payment',
      color: 'green',
      nextStep: 'va-complete'
    },
    {
      id: 'va-complete',
      icon: 'star outline',
      title: 'Project Complete',
      description: 'Leave review & build portfolio',
      color: 'green',
      isEnd: true
    }
  ]

  // Interaction points between the two flows
  const interactionPoints = [
    { bookCreatorStep: 'bc-wait', voiceActorStep: 'va-apply', label: 'Application Submission' },
    { bookCreatorStep: 'bc-accept', voiceActorStep: 'va-receive', label: 'Status Communication' },
    { bookCreatorStep: 'bc-reject', voiceActorStep: 'va-receive', label: 'Rejection Feedback' },
    { bookCreatorStep: 'bc-negotiate', voiceActorStep: 'va-negotiate', label: 'Contract Negotiation' },
    { bookCreatorStep: 'bc-review-delivery', voiceActorStep: 'va-deliver', label: 'Work Delivery' },
    { bookCreatorStep: 'bc-payment', voiceActorStep: 'va-payment', label: 'Payment Processing' }
  ]

  const renderStepCard = (step, isVisible) => {
    const isHovered = hoveredCard === step.id

    return (
      <Transition visible={isVisible} animation='fade' duration={300}>
        <div style={{ marginBottom: '20px' }}>
          <Card
            fluid
            color={step.color}
            raised={isHovered}
            onMouseEnter={() => setHoveredCard(step.id)}
            onMouseLeave={() => setHoveredCard(null)}
            className={`flow-step-card ${step.isDecision ? 'decision-card' : ''} ${step.isInteraction ? 'interaction-card' : ''}`}
          >
            <Card.Content>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon
                    name={step.icon}
                    size='big'
                    color={step.color}
                    className={step.automated ? 'rotating-icon' : ''}
                  />
                  <div style={{ marginLeft: '15px' }}>
                    <Card.Header>{step.title}</Card.Header>
                    <Card.Description>{step.description}</Card.Description>
                  </div>
                </div>
                {step.isDecision && (
                  <div>
                    <Label color='green' size='mini'>
                      <Icon name='check' /> Yes
                    </Label>
                    <Label color='red' size='mini'>
                      <Icon name='times' /> No
                    </Label>
                  </div>
                )}
                {step.isInteraction && (
                  <Popup
                    content='Interaction Point'
                    trigger={<Icon name='exchange' color='yellow' size='large' />}
                  />
                )}
                {step.isWaiting && (
                  <Icon name='hourglass half' color='teal' size='large' loading />
                )}
                {step.isEnd && (
                  <Icon name='flag checkered' color={step.color} size='large' />
                )}
                {step.automated && (
                  <Label color='grey' size='mini'>
                    <Icon name='magic' /> Automated
                  </Label>
                )}
              </div>
            </Card.Content>
            {!step.isEnd && !step.isDecision && step.nextStep && (
              <div style={{ textAlign: 'center', marginTop: '-10px', marginBottom: '10px' }}>
                <Icon name='long arrow alternate down' size='large' color='grey' />
              </div>
            )}
            {step.isDecision && (
              <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '-10px', marginBottom: '10px' }}>
                <div style={{ color: 'green' }}>
                  <Icon name='arrow down' /> Yes
                </div>
                <div style={{ color: 'red' }}>
                  <Icon name='arrow down' /> No
                </div>
              </div>
            )}
          </Card>
        </div>
      </Transition>
    )
  }

  const renderLegend = () => (
    <Segment basic>
      <Header as='h4'>
        <Icon name='info circle' />
        Legend
      </Header>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '10px' }}>
        <Label color='blue'>
          <Icon name='book' /> Book Creator Actions
        </Label>
        <Label color='green'>
          <Icon name='microphone' /> Voice Actor Actions
        </Label>
        <Label color='yellow'>
          <Icon name='question circle' /> Decision Points
        </Label>
        <Label color='purple'>
          <Icon name='handshake' /> Negotiations
        </Label>
        <Label color='teal'>
          <Icon name='clock' /> Waiting States
        </Label>
        <Label color='orange'>
          <Icon name='exchange' /> Interaction Points
        </Label>
      </div>
    </Segment>
  )

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Legend */}
      {renderLegend()}

      <Divider />

      {/* Main Flow Diagram */}
      {activeView === 'full' ? (
        <Grid columns={2} divided stackable>
          <Grid.Column>
            <Segment color='blue' raised>
              <Header as='h2' color='blue' textAlign='center'>
                <Icon name='book' />
                Book Creator Flow
              </Header>
              <Divider />
              <div style={{ padding: '20px' }}>
                {bookCreatorSteps.map(step => renderStepCard(step, true))}
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment color='green' raised>
              <Header as='h2' color='green' textAlign='center'>
                <Icon name='microphone' />
                Voice Actor Flow
              </Header>
              <Divider />
              <div style={{ padding: '20px' }}>
                {voiceActorSteps.map(step => renderStepCard(step, true))}
              </div>
            </Segment>
          </Grid.Column>
        </Grid>
      ) : (
        <Grid columns={1}>
          <Grid.Column>
            <Segment
              color={activeView === 'bookCreator' ? 'blue' : 'green'}
              raised
            >
              <Header
                as='h2'
                color={activeView === 'bookCreator' ? 'blue' : 'green'}
                textAlign='center'
              >
                <Icon name={activeView === 'bookCreator' ? 'book' : 'microphone'} />
                {activeView === 'bookCreator' ? 'Book Creator' : 'Voice Actor'} Flow
              </Header>
              <Divider />
              <div style={{ padding: '20px' }}>
                {activeView === 'bookCreator'
                  ? bookCreatorSteps.map(step => renderStepCard(step, true))
                  : voiceActorSteps.map(step => renderStepCard(step, true))
                }
              </div>
            </Segment>
          </Grid.Column>
        </Grid>
      )}

      {/* Interaction Points Summary */}
      {activeView === 'full' && (
        <>
          <Divider />
          <Segment color='yellow' raised>
            <Header as='h3' textAlign='center'>
              <Icon name='exchange' />
              Key Interaction Points
            </Header>
            <Step.Group fluid>
              {interactionPoints.map((point, index) => (
                <Step key={index}>
                  <Icon name='handshake' color='yellow' />
                  <Step.Content>
                    <Step.Title>{point.label}</Step.Title>
                  </Step.Content>
                </Step>
              ))}
            </Step.Group>
          </Segment>
        </>
      )}
    </div>
  )
}

export default UserFlowDiagram
